import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Code2, 
  Cloud, 
  ShieldCheck, 
  TrendingUp, 
  Headphones, 
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Zap,
  Shield,
  Users,
  Check
} from 'lucide-react';

export default function Services() {
  const servicesList = [
    {
      id: 1,
      title: 'Software Development',
      description: 'Custom software solutions designed to meet your unique business needs and scale effortlessly.',
      icon: <Code2 className="w-6 h-6 text-[#f25c05] group-hover:text-white transition-colors duration-300" />,
      link: '/services/software-development',
      tag: 'Custom Built'
    },
    {
      id: 2,
      title: 'Cloud Solutions',
      description: 'Scalable and secure cloud architecture to accelerate your enterprise digital transformation.',
      icon: <Cloud className="w-6 h-6 text-[#f25c05] group-hover:text-white transition-colors duration-300" />,
      link: '/services/cloud-solutions',
      tag: 'High Scale'
    },
    {
      id: 3,
      title: 'Cyber Security',
      description: 'Protect your digital assets with advanced threat monitoring, defense systems, and best practices.',
      icon: <ShieldCheck className="w-6 h-6 text-[#f25c05] group-hover:text-white transition-colors duration-300" />,
      link: '/services/cyber-security',
      tag: 'Enterprise Defense'
    },
    {
      id: 4,
      title: 'IT Consulting',
      description: 'Strategic technology guidance to optimize infrastructure, reduce costs, and drive growth.',
      icon: <TrendingUp className="w-6 h-6 text-[#f25c05] group-hover:text-white transition-colors duration-300" />,
      link: '/services/it-consulting',
      tag: 'Strategy'
    },
    {
      id: 5,
      title: 'Managed IT Services',
      description: 'Comprehensive 24/7 support and operational management to keep your business running seamlessly.',
      icon: <Headphones className="w-6 h-6 text-[#f25c05] group-hover:text-white transition-colors duration-300" />,
      link: '/services/managed-it-services',
      tag: '24/7 Support'
    }
  ];

  const highlights = [
    { icon: <Zap className="w-5 h-5 text-[#f25c05]" />, title: 'Fast Delivery', desc: 'Agile & rapid execution' },
    { icon: <Shield className="w-5 h-5 text-[#f25c05]" />, title: 'Secure & Compliant', desc: 'Enterprise security' },
    { icon: <Users className="w-5 h-5 text-[#f25c05]" />, title: 'Expert Team', desc: 'Dedicated specialists' },
  ];

  return (
    <div className="w-full bg-white text-slate-800 font-sans min-h-screen selection:bg-[#f25c05] selection:text-white">
      
      {/* ================= SECTION 1: HERO HEADER ================= */}
      <section className="relative pt-20 pb-24 lg:pt-28 lg:pb-32 bg-gradient-to-b from-orange-50/60 via-white to-slate-50/50 overflow-hidden border-b border-orange-100/60">
        
        {/* Subtle Decorative Background Spheres */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-tr from-orange-200/40 to-amber-100/30 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-orange-100/50 rounded-full blur-2xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100/80 border border-orange-200 text-[#f25c05] text-xs font-bold tracking-wider uppercase mb-6 backdrop-blur-md shadow-sm">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Next-Gen Technology Services</span>
          </div>

          {/* Heading */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.15] text-slate-900">
            Empower Your Business With <br className="hidden sm:inline" />
            <span className="bg-gradient-to-r from-[#f25c05] via-orange-500 to-amber-500 bg-clip-text text-transparent">
              Smart IT Solutions
            </span>
          </h1>

          {/* Subtitle */}
          <p className="mt-6 text-base sm:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto font-normal leading-relaxed">
            We build, secure, and scale modern digital products with modern software architecture and proactive IT strategies.
          </p>

          {/* Key Highlights */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto pt-8 border-t border-slate-200/80">
            {highlights.map((item, idx) => (
              <div key={idx} className="flex items-center justify-center gap-3 p-3.5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="p-2 rounded-xl bg-orange-50">
                  {item.icon}
                </div>
                <div className="text-left">
                  <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ================= SECTION 2: SERVICES CARDS ================= */}
      <section className="py-20 lg:py-28 relative bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 border-b border-slate-200 pb-8">
            <div>
              <span className="text-[#f25c05] text-xs font-extrabold tracking-widest uppercase bg-orange-50 px-3 py-1 rounded-md border border-orange-100">
                What We Offer
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mt-3 tracking-tight">
                Architected for Performance & Growth
              </h2>
            </div>
            <p className="text-slate-600 text-sm max-w-md leading-relaxed">
              Tailored services engineered to solve complex operational challenges and elevate your digital footprint.
            </p>
          </div>

          {/* Responsive Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesList.map((service) => (
              <div 
                key={service.id}
                className="group relative bg-white border border-slate-200/80 hover:border-[#f25c05] rounded-3xl p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/10 flex flex-col justify-between overflow-hidden"
              >
                {/* Top Accent Line on Hover */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#f25c05] to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div>
                  {/* Card Header Info */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center group-hover:bg-[#f25c05] group-hover:border-[#f25c05] shadow-sm transition-all duration-300">
                      {service.icon}
                    </div>
                    <span className="text-[11px] font-bold text-[#f25c05] bg-orange-50 px-3 py-1 rounded-full border border-orange-100">
                      {service.tag}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-extrabold text-slate-900 mb-3 group-hover:text-[#f25c05] transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed mb-8">
                    {service.description}
                  </p>
                </div>

                {/* Explore Link */}
                <div className="pt-4 border-t border-slate-100">
                  <Link 
                    to={service.link}
                    className="inline-flex items-center gap-2 text-sm font-bold text-[#f25c05] hover:text-orange-600 transition-colors group-hover:gap-3 duration-200"
                  >
                    <span>Explore Service</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}

            {/* Custom Interactive Proposal Card */}
            <div className="bg-gradient-to-br from-[#f25c05] via-orange-600 to-amber-600 rounded-3xl p-8 flex flex-col justify-between text-white shadow-xl shadow-orange-500/20 relative overflow-hidden group">
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-xl pointer-events-none" />
              
              <div>
                <span className="text-xs uppercase font-bold tracking-widest text-orange-200 bg-black/10 px-3 py-1 rounded-full inline-block mb-3">
                  Custom Need?
                </span>
                <h3 className="text-2xl font-black mt-2 mb-4 leading-snug">
                  Have a Unique Challenge?
                </h3>
                <p className="text-orange-100 text-sm leading-relaxed">
                  We create fully personalized solutions tailored specifically to your company structure and goals.
                </p>
              </div>

              <div className="pt-8 relative z-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center w-full bg-white hover:bg-orange-50 text-slate-900 font-bold text-sm py-3.5 px-6 rounded-2xl transition-all duration-200 shadow-lg gap-2"
                >
                  <span>Request Custom Proposal</span>
                  <ArrowRight className="w-4 h-4 text-[#f25c05]" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= SECTION 3: CALL TO ACTION (CTA) ================= */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border border-slate-800 p-8 sm:p-12 lg:p-16 overflow-hidden shadow-2xl text-white">
            
            {/* Soft Glow Circles */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#f25c05]/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
              
              {/* Text Area */}
              <div className="space-y-4 max-w-2xl text-center lg:text-left">
                <div className="inline-flex items-center gap-2 text-[#f25c05] font-bold text-xs uppercase tracking-wider bg-orange-500/10 px-3.5 py-1.5 rounded-full border border-orange-500/20">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Let's Build Together</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight">
                  Ready to accelerate your digital journey?
                </h2>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  Connect with our technology consultants today to plan, design, and execute software solutions built for long-term scalability.
                </p>
                
                {/* Checkmarks */}
                <div className="pt-2 flex flex-wrap justify-center lg:justify-start gap-6 text-xs sm:text-sm text-slate-300 font-medium">
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#f25c05]" /> Free Initial Review
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#f25c05]" /> Dedicated Engineers
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="shrink-0 w-full sm:w-auto">
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 bg-[#f25c05] hover:bg-orange-600 text-white font-bold text-base px-8 py-4 rounded-2xl shadow-xl shadow-orange-500/30 hover:scale-105 transition-all duration-300"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
}